import { bankApi } from '@/api'
import { IEmptyResponse, IPageInfoRequest, IPageInfoResponse } from '@/domains/CommonDomain'
import {
  DebitCardBrand,
  DebitCardDesignTypeCode,
  DebitCardStatusTypeCode,
  DebitCardTransactionFilterTypeCode,
  DebitCardVersion,
  IDebitCardEzlAvailableIssueResponse,
  IDebitCardEzlInquiryResponse,
  IDebitCardInfo,
  IDebitCardTransaction,
  IDebitCardWithAmount,
  ISimpleAddress,
  OrderingTypeCode,
} from '@/domains/DebitCardDomain'

const BASE = '/debit-card'

export const DEBIT_CARD_API_URL = {
  ACTIVATE: 'debit-card/issue/activate',
  REPORT_LOST: 'debit-card/report-lost',
  CANCEL_REPORT_LOST: 'debit-card/report-lost/cancel',
  DELETE_DEBIT_CARD: 'debit-card/close',
  NEW_ISSUE_DEBIT_CARD: 'debit-card/issue/new-issue',
  RE_ISSUE_DEBIT_CARD: 'debit-card/issue/re-issue',
  CHANGE_OPTION: {
    DOMESTIC_USE: 'debit-card/change-option/domestic-use',
    OVERSEA_USE: 'debit-card/change-option/oversea-use',
  },
  CHANGE_LIMIT: {
    ONE_TIME_LIMIT: 'debit-card/change-limit/one-time-limit',
    ONE_DAY_LIMIT: 'debit-card/change-limit/one-day-limit',
  },
}

export interface IDebitCardMainResponse {
  debitCardWithAmountList: IDebitCardWithAmount[]
  totalAmountForMonth: string
}

export const fetchDebitCardList = (): Promise<IDebitCardMainResponse> => {
  return bankApi.post(`${BASE}/main`)
}

export interface IDebitCardShowInfoRequest {
  acctNo: string
  debitCardNo: string
}

export interface IDebitCardShowInfoResponse {
  debitCardInfoList: IDebitCardInfo[]
  selectedCardIndex: number
}

export const fetchDebitCardInfo = (
  data: IDebitCardShowInfoRequest,
): Promise<IDebitCardShowInfoResponse> => {
  return bankApi.post(`${BASE}/show/info`, data)
}

export interface IDebitCardReportLostResponse {
  autoClosingDate: number
  debitCardStatusTypeCode: DebitCardStatusTypeCode
}

export interface IDebitCardCancelReportLostResponse {
  debitCardStatusTypeCode: DebitCardStatusTypeCode
}

export interface IDebitCardTransactionFilter {
  debitCardTransactionFilterTypeCode: DebitCardTransactionFilterTypeCode
  orderingTypeCode: OrderingTypeCode
}

export interface IDebitCardMonthlyTransactionHistoryWithBriefRequest {
  debitCardNo: string
  filter: IDebitCardTransactionFilter
  pageInfoRequest: IPageInfoRequest
  targetDateString: string
}

export interface IMonthlyBrief {
  totalBenefitAmount: string
  totalBenefitCashBackAmount: string
  totalBenefitFeeFreeAmount: string
  totalTransactionAmount: string
}

export interface IDebitCardMonthlyTransactionHistoryWithBriefResponse {
  debitCardNo: string
  debitCardTransactionList: IDebitCardTransaction[]
  monthlyBrief: IMonthlyBrief
  pageInfo: IPageInfoResponse
}

export const fetchDebitCardTransactionWithBenefit = (
  data: IDebitCardMonthlyTransactionHistoryWithBriefRequest,
): Promise<IDebitCardMonthlyTransactionHistoryWithBriefResponse> => {
  return bankApi.post(`${BASE}/show/transaction-history/monthly/with-brief`, data)
}

export interface IDebitCardIssueResponse {
  acctNo: number
  customerName: string
  debitCardDesignTypeCode: DebitCardDesignTypeCode
  debitCardNo: number
  debitCardStatusTypeCode: DebitCardStatusTypeCode
  debitCardVersion: DebitCardVersion
  debitCardBrand: DebitCardBrand
  expiredDateString: string
  openDateString: string
  shippingAddress: ISimpleAddress
}

export interface IDebitCardCheckAvailableDeliveryRequest {
  cityCode: string
  provinceCode: string
}

export const checkAvailableDelivery = async (
  data: IDebitCardCheckAvailableDeliveryRequest,
): Promise<boolean> => {
  try {
    await bankApi.post(`${BASE}/issue/available-delivery/check`, data)

    return false
  } catch (e) {
    return true
  }
}

export interface IDebitCardCheckIssuanceFeeRequest {
  acctNo: string
  debitCardNo?: string | null
  debitCardDesignTypeCode?: DebitCardDesignTypeCode
  debitCardVersion?: DebitCardVersion
  debitCardBrand?: DebitCardBrand
  cityCode?: string
  provinceCode?: string
}

export interface IDebitCardCheckIssuanceFeeResponse {
  issuanceFee: string
  defaultIssuanceFee: string
}

export const checkIssuanceFee = (
  data?: IDebitCardCheckIssuanceFeeRequest,
): Promise<IDebitCardCheckIssuanceFeeResponse> => {
  return bankApi.post(`${BASE}/issue/issuance-fee/check`, data)
}

export interface IDebitCardEzlDeliveryCompleteRequest {
  debitCardNo: string
  debitCardStatus?: DebitCardStatusTypeCode
}

export const isEzlDeliveryComplete = (
  data: IDebitCardEzlDeliveryCompleteRequest,
): Promise<IEmptyResponse> => {
  return bankApi.post(`${BASE}/ezl-topup/delivery-completed`, data)
}

export interface IDebitCardEzlInquiryRequest {
  medNo: string // EZL Card number
}

export const ezlInquiry = (
  data: IDebitCardEzlInquiryRequest,
): Promise<IDebitCardEzlInquiryResponse> => {
  return bankApi.post(`${BASE}/ezl-inquiry`, data)
}

export const inquiryEzlCardIssuanceStatus = (): Promise<IDebitCardEzlAvailableIssueResponse> => {
  return bankApi.post(`${BASE}/issue/ezl-available`)
}
